<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="section-container">
	<section class="sub-section">
		<div class="sub-section-header">
			<span class="sub-section-title">2. Application selection</span>

			<span class="sub-section-description">
				Select the application ID and period window that you need to view your scenarios
			</span>
		</div>

		<form [formGroup]="applicationForm">
			<div class="form-container">
				<div class="adjacent-inputs">
					<globalictcc-dropdown
						label="Application ID"
						formControlName="applicationId"
						[options]="applicationOptions"
						[paginated]="true"
						[hasMore]="hasMoreApplications"
						[isLoadingMore]="isApplicationsLoadingMore"
						(searchChange)="onApplicationSearch($event)"
						(loadMore)="onApplicationLoadMore()"
					/>

					<globalictcc-dropdown
						label="Prediction window"
						formControlName="predictionWindow"
						[options]="predictionWindows"
					/>

					<globalictcc-button
						label="Apply"
						(handleClick)="onApplicationApplyClick()"
						[disabled]="applicationForm.invalid"
					/>
				</div>
			</div>
		</form>

		@if (isApplicationFormApplied) {
			<globalictcc-chart
				chartType="barX"
				[isTitleVisible]="false"
				[data]="scenariosDifferencesBarChart?.chart"
				[isLoading]="isScenariosDifferencesBarChartLoading"
				[hasError]="scenariosDifferencesBarChart?.hasError"
				[configOverrides]="scenariosDifferencesBarChartOverrides"
				[patterns]="scenariosDifferencesBarChartPatterns"
				(handleChartRefresh)="onApplicationApplyClick()"
			/>
		}
	</section>

	<section
		class="sub-section"
		[ngClass]="{ disabled: !isApplicationFormApplied }"
	>
		<div class="sub-section-header">
			<span class="sub-section-title">3. Baseline scenario</span>

			<span class="sub-section-description">
				The forecasted emissions of your application's layers are presented below
			</span>
		</div>

		@if (isApplicationFormApplied && baselineScenarioLayers.length) {
			<div class="application-layers">
				<globalictcc-accordion-group>
					<globalictcc-accordion
						title="Baseline Application Layers - Configuration"
						[isExpanded]="true"
					>
						<div class="layers-configurations">
							@for (layer of baselineScenarioLayers; track $index) {
								<globalictcc-layer-configuration
									[layerConfigurationInput]="layer"
								/>
							}
						</div>
					</globalictcc-accordion>
				</globalictcc-accordion-group>
			</div>

			<globalictcc-chart
				chartType="barX"
				[data]="baselineScenarioLayersBarChart?.chart"
				[isLoading]="isBaselineScenarioLayersBarChartLoading"
				[hasError]="baselineScenarioLayersBarChart?.hasError"
				[configOverrides]="baselineScenarioLayersBarChartOverrides"
				(handleChartRefresh)="onApplicationApplyClick()"
			/>
		}
	</section>

	<section
		class="sub-section"
		[ngClass]="{ disabled: !isApplicationFormApplied }"
	>
		<div class="sub-section-header">
			<span class="sub-section-title">4. Scenarios</span>

			<span class="sub-section-description">Create, Edit and View your scenarios</span>
		</div>

		<div class="scenarios-actions">
			@if (scenariosChips.length) {
				<globalictcc-chips
					[chips]="scenariosChips"
					(handleSelection)="onScenarioClick($event)"
				/>
			}

			<globalictcc-button
				iconName="plus"
				iconPosition="left"
				label="Add new scenario"
				(handleClick)="onAddNewScenario()"
				[disabled]="!isApplicationFormApplied"
			/>
		</div>

		@if (selectedScenario) {
			<globalictcc-accordion-group class="layers-edit-configurations">
				<globalictcc-accordion
					title="Application Layers - Configuration"
					[isExpanded]="true"
				>
					<div class="scenarios-app-layers-actions">
						@if (isEditable) {
							<span>
								<globalictcc-button
									label="Edit configuration for all layers"
									[textOnly]="true"
									iconName="edit"
									(handleClick)="onEdit()"
								/>
							</span>
						}

						<span>
							<globalictcc-button
								label="Delete the scenario"
								[textOnly]="true"
								iconName="delete"
								(handleClick)="onDeleteScenario()"
							/>
						</span>
					</div>

					<div class="layers-configurations">
						@for (layer of customScenarioLayers; track $index) {
							<globalictcc-layer-configuration
								[layerConfigurationInput]="layer"
								[isEditable]="isEditable"
								(handleEditLayer)="onEdit($event)"
							/>
						}
					</div>
				</globalictcc-accordion>
			</globalictcc-accordion-group>
		}
	</section>

	@if (isScenarioVisible) {
		<section class="sub-section">
			<div class="sub-section-header">
				<span class="sub-section-title">
					5. Compare my scenarios with my baseline scenario
				</span>

				<span class="sub-section-description">
					Select a created scenario and run the simulation by clicking the RUN button
				</span>
			</div>

			<form
				[formGroup]="scenarioRunForm"
				class="form-container"
			>
				<div class="adjacent-inputs">
					<globalictcc-dropdown
						label="Scenario"
						formControlName="scenario"
						[options]="scenarioRunOptions"
					/>

					@if (isRunScenarioButtonVisible) {
						<globalictcc-button
							label="Run"
							(handleClick)="onScenarioRun()"
							[disabled]="
								scenarioRunForm.invalid ||
								isBaselineScenarioComparisonBarChartLoading
							"
						/>
					}
				</div>
			</form>

			@if (isScenarioRunned) {
				<globalictcc-chart
					chartType="barX"
					[isTitleVisible]="false"
					[isLegendVisible]="true"
					[data]="baselineScenarioComparisonBarChart?.chart"
					[isLoading]="isBaselineScenarioComparisonBarChartLoading"
					[hasError]="baselineScenarioComparisonBarChart?.hasError"
					[configOverrides]="baselineScenarioComparisonBarChartOverrides"
					[patterns]="baselineScenarioComparisonBarChartPatterns"
					(handleChartRefresh)="onBaselineScenarioComparisonBarChartRefresh()"
				/>
			}
		</section>
	}
</section>

<!-- Dialogs content -->
<ng-template #newScenarioDialogContent>
	<form [formGroup]="newScenarioForm">
		<globalictcc-dropdown
			label="Based on"
			formControlName="startingScenario"
			class="fill"
			[options]="scenariosOptions"
		/>

		<globalictcc-input
			label="Name"
			formControlName="scenarioName"
			class="fill"
		/>
	</form>
</ng-template>
